import React, { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import apiClient from '../../api/client';
import { Check, X, Delete, RotateCcw, User, Clock, Calendar } from 'lucide-react';

/**
 * INSIGHT GROUP PT — Premium Tablet Kiosk (Dark + Orange Accents)
 *
 * 핵심 원칙:
 * - 기존 state(step/digits/members/selectedMember/walkinClasses/result)와
 *   API 호출(/check-in/search, /check-in/walkin-classes, /check-in/walkin-attend, /check-in/attend)은 그대로 유지
 * - h-screen + overflow-hidden + 절대 스크롤 없는 풀스크린 키오스크 UI
 * - 세로 태블릿(iPad/Android 9.7"~12") 최적화
 */

const BRAND_BG = 'https://static.prod-images.emergentagent.com/jobs/9a520dfa-a648-454b-ab4b-4675a972bdec/images/45c126f30b4adf126392ebb1932ed4ec96030b151b061cecee6ddb8d3ecad203.png';

const CheckIn = () => {
  // ===== 기존 state (절대 변경 금지) =====
  const [digits, setDigits] = useState('');
  const [step, setStep] = useState('input');
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [walkinClasses, setWalkinClasses] = useState([]);
  const [walkinPassId, setWalkinPassId] = useState(null);
  const [result, setResult] = useState(null);

  // ===== Live clock (KST) =====
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(t);
  }, []);
  const clockStr = now.toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit', hour12: false });
  const dateStr = new Intl.DateTimeFormat('ko-KR', { timeZone: 'Asia/Seoul', month: 'long', day: 'numeric', weekday: 'short' }).format(now);

  // ===== Auto-reset =====
  useEffect(() => {
    if (step === 'result' && result) {
      const timer = setTimeout(() => { resetAll(); }, 3000);
      return () => clearTimeout(timer);
    }
  }, [step, result]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== Click sound (Web Audio API, no external file) =====
  const audioCtxRef = useRef(null);
  const playClick = useCallback((variant = 'tap') => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      // Resume if suspended (iOS Safari requires user gesture)
      if (ctx.state === 'suspended') ctx.resume();

      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      // Variant tuning
      const cfg = {
        tap:     { freq: 880,  dur: 0.045, peak: 0.18 }, // 숫자 키
        soft:    { freq: 520,  dur: 0.055, peak: 0.14 }, // 삭제/초기화
        confirm: { freq: 1320, dur: 0.10,  peak: 0.22 }, // 출석하기 확정
      }[variant] || { freq: 880, dur: 0.045, peak: 0.18 };

      osc.type = 'square';
      osc.frequency.setValueAtTime(cfg.freq, now);
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(cfg.peak, now + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + cfg.dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + cfg.dur + 0.02);
    } catch (e) { /* silent */ }
  }, []);

  const resetAll = useCallback(() => {
    setDigits('');
    setStep('input');
    setMembers([]);
    setSelectedMember(null);
    setWalkinClasses([]);
    setWalkinPassId(null);
    setResult(null);
  }, []);

  const handleDigit = (digit) => {
    if (digits.length < 4) {
      playClick('tap');
      setDigits((prev) => prev + digit);
    }
  };
  const handleBackspace = () => {
    if (digits.length === 0) return;
    playClick('soft');
    setDigits((prev) => prev.slice(0, -1));
  };

  // ===== Search by phone last 4 (unchanged API) =====
  const handleSearch = async () => {
    if (digits.length !== 4) {
      toast.error('4자리를 입력해주세요');
      return;
    }
    playClick('confirm');
    setLoading(true);
    try {
      const response = await apiClient.post('/check-in/search', { phone_last4: digits });
      const foundMembers = response.data.members || [];
      if (foundMembers.length === 0) {
        setResult({ success: false, message: '회원을 찾을 수 없습니다' });
        setStep('result');
      } else if (foundMembers.length === 1) {
        handleMemberSelect(foundMembers[0]);
      } else {
        setMembers(foundMembers);
        setStep('selectMember');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || '회원 조회 실패';
      setResult({ success: false, message: errorMsg });
      setStep('result');
    } finally {
      setLoading(false);
    }
  };

  const handleMemberSelect = (member) => {
    setSelectedMember(member);
    const attendableClasses = (member.today_classes || []).filter((c) => c.status !== 'attended');
    if (attendableClasses.length === 0) {
      if (member.today_classes?.some((c) => c.status === 'attended')) {
        setResult({ success: false, message: '이미 출석 처리되었습니다' });
        setStep('result');
        return;
      }
      loadWalkinClasses(member);
    } else if (attendableClasses.length === 1) {
      handleAttend(member.member_id, attendableClasses[0].session_id);
    } else {
      setStep('selectClass');
    }
  };

  const loadWalkinClasses = async (member) => {
    setLoading(true);
    try {
      const response = await apiClient.post('/check-in/walkin-classes', { member_id: member.member_id });
      const { has_active_pass, active_pass_id, classes } = response.data;
      if (!has_active_pass) {
        setResult({ success: false, title: '사용 가능한 이용권이 없습니다', message: '직원에게 문의해주세요' });
        setStep('result');
        return;
      }
      if (!classes || classes.length === 0) {
        setResult({ success: false, title: '예약 가능한 수업이 없습니다', message: '오늘 참여 가능한 수업이 없어요' });
        setStep('result');
        return;
      }
      setWalkinClasses(classes);
      setWalkinPassId(active_pass_id);
      setStep('selectWalkinClass');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || '워크인 수업 조회 실패';
      setResult({ success: false, message: errorMsg });
      setStep('result');
    } finally {
      setLoading(false);
    }
  };

  const handleWalkinAttend = async (sessionId) => {
    if (!selectedMember) return;
    setLoading(true);
    try {
      const response = await apiClient.post('/check-in/walkin-attend', {
        member_id: selectedMember.member_id,
        session_id: sessionId,
        pass_id: walkinPassId || undefined,
      });
      setResult({
        success: true,
        message: '출석 완료되었습니다',
        memberName: response.data.member_name,
        className: response.data.class_name,
      });
      setStep('result');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || '출석 처리 실패';
      setResult({ success: false, message: errorMsg });
      setStep('result');
    } finally {
      setLoading(false);
    }
  };

  const handleAttend = async (memberId, sessionId) => {
    setLoading(true);
    try {
      const response = await apiClient.post('/check-in/attend', { member_id: memberId, session_id: sessionId });
      setResult({
        success: true,
        message: '출석 완료되었습니다',
        memberName: response.data.member_name,
        className: response.data.class_name,
      });
      setStep('result');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || '출석 처리 실패';
      setResult({ success: false, message: errorMsg });
      setStep('result');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // RENDER COMPONENTS
  // ============================================================

  // --- KioskHero (상단 ~25%) ---
  const KioskHero = () => (
    <div className="relative flex-[0.25] w-full overflow-hidden">
      <img
        src={BRAND_BG}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-55"
        draggable="false"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#05070B]/30 via-[#05070B]/75 to-[#05070B]" />
      {/* Orange glow accent */}
      <div className="absolute top-0 right-0 w-[60%] h-[80%] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at top right, rgba(255,122,0,0.25), transparent 60%)' }}
      />
      <div className="relative z-10 h-full flex flex-col justify-between px-6 md:px-10 py-5 md:py-7">
        <div className="flex items-start justify-between">
          <div data-testid="kiosk-brand">
            <div className="text-white text-xl md:text-2xl font-black tracking-tight leading-none">INSIGHT</div>
            <div className="text-white/60 text-[10px] md:text-xs tracking-[0.3em] mt-1 uppercase">Group PT</div>
          </div>
          <div className="text-right" data-testid="kiosk-clock">
            <div className="text-white text-2xl md:text-3xl font-bold tracking-tight tabular-nums leading-none">{clockStr}</div>
            <div className="text-white/50 text-[10px] md:text-xs mt-1">{dateStr}</div>
          </div>
        </div>
        <div>
          <div className="text-white text-2xl md:text-4xl font-black uppercase tracking-tight leading-[1.05]">
            Train Today,
            <br />
            <span className="text-[#FF7A00]" style={{ textShadow: '0 0 30px rgba(255,122,0,0.5)' }}>
              Be Stronger Tomorrow.
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // --- PinDisplay (중앙 ~20%) ---
  const PinDisplay = () => {
    const activeIdx = digits.length;
    return (
      <div className="flex-[0.20] w-full flex flex-col items-center justify-center px-6">
        <p className="text-white/75 text-xl md:text-2xl font-semibold tracking-wide mb-4 md:mb-6" data-testid="pin-instruction">
          출석번호 4자리를 입력하세요
        </p>
        <div className="flex gap-4 md:gap-6" data-testid="pin-display">
          {[0, 1, 2, 3].map((i) => {
            const filled = !!digits[i];
            const isActive = i === activeIdx;
            return (
              <div
                key={i}
                data-testid={`pin-slot-${i}`}
                className={[
                  'w-20 h-24 md:w-24 md:h-28 rounded-2xl flex items-center justify-center text-5xl md:text-6xl font-bold tabular-nums transition-all duration-200',
                  filled
                    ? 'bg-[#FF7A00]/10 border-2 border-[#FF7A00] text-white shadow-[0_0_30px_rgba(255,122,0,0.3)]'
                    : isActive
                      ? 'bg-white/5 border-2 border-white/30 text-white/40 animate-pulse'
                      : 'bg-white/[0.03] border-2 border-white/10 text-white/20',
                ].join(' ')}
              >
                {digits[i] || '•'}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // --- Keypad (하단 ~50%, 화면 폭 94% 사용) ---
  const Keypad = () => {
    const NumBtn = ({ n }) => (
      <button
        type="button"
        onClick={() => handleDigit(String(n))}
        disabled={loading}
        data-testid={`keypad-${n}`}
        className="h-[100px] md:h-[120px] rounded-2xl bg-white/[0.05] border border-white/[0.08] text-white text-5xl md:text-6xl font-bold tabular-nums hover:bg-white/[0.10] active:bg-[#FF7A00]/30 active:scale-[0.97] active:border-[#FF7A00]/60 transition-all flex items-center justify-center disabled:opacity-50"
      >
        {n}
      </button>
    );

    return (
      <div className="flex-[0.50] w-[94%] mx-auto pt-2 pb-2 flex flex-col gap-3 md:gap-4">
        <div className="grid grid-cols-3 gap-3 md:gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <NumBtn key={n} n={n} />
          ))}
          <button
            type="button"
            onClick={() => { playClick('soft'); resetAll(); }}
            disabled={loading}
            data-testid="keypad-reset"
            className="h-[100px] md:h-[120px] rounded-2xl bg-transparent text-white/55 hover:text-white/90 active:text-[#FF7A00] active:scale-[0.97] transition-all flex flex-col items-center justify-center gap-1.5"
          >
            <RotateCcw className="w-7 h-7 md:w-8 md:h-8" />
            <span className="text-xs md:text-sm uppercase tracking-wider font-medium">초기화</span>
          </button>
          <NumBtn n={0} />
          <button
            type="button"
            onClick={handleBackspace}
            disabled={loading || digits.length === 0}
            data-testid="keypad-delete"
            className="h-[100px] md:h-[120px] rounded-2xl bg-transparent text-white/55 hover:text-white/90 active:text-[#FF7A00] active:scale-[0.97] transition-all flex flex-col items-center justify-center gap-1.5 disabled:opacity-30"
          >
            <Delete className="w-7 h-7 md:w-8 md:h-8" />
            <span className="text-xs md:text-sm uppercase tracking-wider font-medium">삭제</span>
          </button>
        </div>

        {/* Submit button — full width matching keypad */}
        <button
          type="button"
          onClick={handleSearch}
          disabled={digits.length !== 4 || loading}
          data-testid="kiosk-checkin-btn"
          className={[
            'w-full h-[88px] md:h-[100px] rounded-2xl text-2xl md:text-3xl font-bold uppercase tracking-wider transition-all flex items-center justify-center',
            digits.length === 4 && !loading
              ? 'bg-[#FF7A00] text-white shadow-[0_0_40px_rgba(255,122,0,0.4)] hover:brightness-110 active:scale-[0.98]'
              : 'bg-white/[0.05] text-white/30 cursor-not-allowed border border-white/5',
          ].join(' ')}
        >
          {loading ? '조회 중…' : '출석하기'}
        </button>
      </div>
    );
  };

  // --- Selection cards (Member / Class / Walkin) ---
  const SelectListLayout = ({ title, subtitle, children, testId }) => (
    <div className="absolute inset-0 z-30 flex flex-col bg-[#05070B]/95 backdrop-blur-xl">
      <div className="px-8 md:px-12 pt-10 md:pt-14 pb-6">
        <div className="text-[#FF7A00] text-xs md:text-sm tracking-[0.3em] uppercase mb-2">INSIGHT</div>
        <div className="text-white text-3xl md:text-4xl font-black uppercase tracking-tight" data-testid={testId}>{title}</div>
        {subtitle && <div className="text-white/60 text-base md:text-lg mt-2">{subtitle}</div>}
      </div>
      <div className="flex-1 overflow-y-auto px-6 md:px-10 pb-6">
        <div className="max-w-2xl mx-auto space-y-3 md:space-y-4">{children}</div>
      </div>
      <div className="px-6 md:px-10 pb-8 pt-2">
        <button
          type="button"
          onClick={resetAll}
          className="w-full max-w-2xl mx-auto h-14 md:h-16 rounded-2xl bg-white/[0.04] border border-white/[0.08] text-white/80 text-base md:text-lg font-medium hover:bg-white/[0.08] active:scale-[0.99] transition-all flex items-center justify-center"
          data-testid="kiosk-back-btn"
        >
          처음으로
        </button>
      </div>
    </div>
  );

  const MemberSelectView = () => (
    <SelectListLayout title="회원 선택" subtitle="본인을 선택해주세요" testId="member-select-title">
      {members.map((m) => (
        <button
          key={m.member_id}
          type="button"
          onClick={() => handleMemberSelect(m)}
          className="w-full text-left bg-white/[0.04] border border-white/[0.08] rounded-2xl p-5 md:p-6 hover:bg-white/[0.07] active:scale-[0.99] active:border-[#FF7A00] transition-all"
          data-testid={`kiosk-member-${m.member_id}`}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#FF7A00]/15 border border-[#FF7A00]/30 flex items-center justify-center">
              <User className="w-6 h-6 md:w-7 md:h-7 text-[#FF7A00]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-lg md:text-xl font-bold">{m.name}</div>
              <div className="text-white/40 text-xs md:text-sm">{m.member_number} · {m.phone}</div>
              {m.today_classes?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {m.today_classes.slice(0, 2).map((cls, idx) => (
                    <span
                      key={idx}
                      className={[
                        'text-[10px] md:text-xs px-2 py-0.5 rounded-full border',
                        cls.status === 'attended' ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300'
                          : cls.status === 'in_progress' ? 'bg-[#FF7A00]/15 border-[#FF7A00]/40 text-[#FF7A00]'
                          : 'bg-white/[0.06] border-white/10 text-white/70',
                      ].join(' ')}
                    >
                      {cls.start_time} {cls.class_name}{cls.status === 'attended' && ' · 완료'}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </button>
      ))}
    </SelectListLayout>
  );

  const ClassCard = ({ cls, onClick, testId }) => {
    const status = cls.status;
    const isLive = status === 'in_progress';
    const isGrace = status === 'ended_within_grace';
    const statusLabel = isLive ? '진행중' : status === 'upcoming' ? '예정' : isGrace ? '종료 직후' : '';
    return (
      <button
        type="button"
        onClick={onClick}
        className={[
          'w-full text-left rounded-2xl p-5 md:p-6 border transition-all active:scale-[0.99]',
          isLive
            ? 'bg-[#FF7A00]/10 border-[#FF7A00]/40 hover:bg-[#FF7A00]/15'
            : 'bg-white/[0.04] border-white/[0.08] hover:bg-white/[0.07] active:border-[#FF7A00]',
        ].join(' ')}
        data-testid={testId}
      >
        <div className="flex items-center gap-4">
          <div className={[
            'w-12 h-12 md:w-14 md:h-14 rounded-full border flex items-center justify-center',
            isLive ? 'bg-[#FF7A00]/20 border-[#FF7A00]/40' : 'bg-white/[0.06] border-white/10',
          ].join(' ')}>
            {isLive
              ? <Clock className="w-6 h-6 md:w-7 md:h-7 text-[#FF7A00]" />
              : <Calendar className="w-6 h-6 md:w-7 md:h-7 text-white/70" />
            }
          </div>
          <div className="flex-1">
            <div className="text-white text-lg md:text-xl font-bold">{cls.class_name}</div>
            <div className="text-white/50 text-xs md:text-sm">
              {cls.start_time} ~ {cls.end_time}
              {cls.max_capacity ? <span className="ml-2 text-white/30">({cls.current_count}/{cls.max_capacity})</span> : null}
            </div>
          </div>
          {statusLabel && (
            <span className={[
              'px-2.5 py-1 rounded-full text-xs font-medium border',
              isLive ? 'bg-[#FF7A00]/20 border-[#FF7A00]/50 text-[#FF7A00]'
                : isGrace ? 'bg-amber-500/15 border-amber-500/30 text-amber-300'
                : 'bg-white/[0.06] border-white/10 text-white/70',
            ].join(' ')}>
              {statusLabel}
            </span>
          )}
        </div>
      </button>
    );
  };

  const ClassSelectView = () => {
    const attendableClasses = (selectedMember?.today_classes || []).filter((c) => c.status !== 'attended');
    return (
      <SelectListLayout title="수업 선택" subtitle={`${selectedMember?.name}님, 출석할 수업을 선택해주세요`} testId="class-select-title">
        {attendableClasses.map((cls) => (
          <ClassCard
            key={cls.reservation_id}
            cls={cls}
            onClick={() => handleAttend(selectedMember.member_id, cls.session_id)}
            testId={`kiosk-class-${cls.session_id}`}
          />
        ))}
      </SelectListLayout>
    );
  };

  const WalkinClassSelectView = () => (
    <SelectListLayout title="오늘 예약이 없습니다" subtitle={`${selectedMember?.name}님, 참여할 수업을 선택해주세요`} testId="walkin-title">
      {walkinClasses.map((cls) => (
        <ClassCard
          key={cls.session_id}
          cls={cls}
          onClick={() => handleWalkinAttend(cls.session_id)}
          testId={`walkin-class-${cls.session_id}`}
        />
      ))}
    </SelectListLayout>
  );

  // --- Result overlay ---
  const ResultOverlay = () => {
    const success = !!result?.success;
    return (
      <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-[#05070B]/97 backdrop-blur-2xl">
        <div className="relative">
          <div
            className={[
              'w-32 h-32 md:w-40 md:h-40 rounded-full border-2 flex items-center justify-center',
              success
                ? 'bg-[#FF7A00]/10 border-[#FF7A00] shadow-[0_0_80px_rgba(255,122,0,0.4)]'
                : 'bg-red-500/10 border-red-500 shadow-[0_0_80px_rgba(239,68,68,0.4)]',
            ].join(' ')}
          >
            {success
              ? <Check className="w-16 h-16 md:w-20 md:h-20 text-[#FF7A00]" strokeWidth={2.5} />
              : <X className="w-16 h-16 md:w-20 md:h-20 text-red-400" strokeWidth={2.5} />
            }
          </div>
        </div>
        <div className="mt-10 md:mt-12 text-center px-8 max-w-xl">
          <div className="text-white text-3xl md:text-4xl font-black uppercase tracking-tight" data-testid="result-title">
            {result?.title || result?.message}
          </div>
          {result?.title && result?.message && (
            <div className="text-white/60 text-base md:text-lg mt-3" data-testid="result-subtitle">{result.message}</div>
          )}
          {success && result?.memberName && (
            <div className="mt-6 space-y-1">
              <div className="text-[#FF7A00] text-xl md:text-2xl font-bold">{result.memberName}님</div>
              {result.className && <div className="text-white/50 text-sm md:text-base">{result.className}</div>}
            </div>
          )}
        </div>
        <div className="mt-12 flex flex-col items-center gap-4">
          <div className="text-white/30 text-xs tracking-widest uppercase">3초 후 자동 초기화</div>
          <button
            type="button"
            onClick={resetAll}
            className="px-10 h-12 rounded-2xl bg-white/[0.05] border border-white/10 text-white/80 text-sm font-medium tracking-wider uppercase hover:bg-white/10 transition-all"
            data-testid="result-reset-btn"
          >
            지금 초기화
          </button>
        </div>
      </div>
    );
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================
  return (
    <div className="h-screen w-screen overflow-hidden bg-[#05070B] text-white flex flex-col touch-manipulation select-none relative">
      {/* Always-rendered base layout (input view) */}
      <KioskHero />
      <PinDisplay />
      <Keypad />
      {/* Bottom safe area ~5% */}
      <div className="flex-[0.05] w-full" />

      {/* Overlays */}
      {step === 'selectMember' && <MemberSelectView />}
      {step === 'selectClass' && <ClassSelectView />}
      {step === 'selectWalkinClass' && <WalkinClassSelectView />}
      {step === 'result' && <ResultOverlay />}
    </div>
  );
};

export default CheckIn;
